"use client";

import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { canUseMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { getStoredMerchantPosBranchId } from "@/modules/merchant/presentation/lib/merchant-pos-branch-storage";
import { hasShopFeature } from "@/modules/merchant/presentation/lib/has-shop-feature";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Badge, Button, Input, cn } from "@devhop/ui";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  FileTextIcon,
  PhoneIcon,
  PlusIcon,
  SearchIcon,
  UserIcon,
  WrenchIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import type { WorkshopJobStatusDTO } from "../../domain/contracts";
import type { WorkshopJobListRow } from "../api/client";
import { useWorkshopJobsQuery } from "../api/queries";
import {
  formatWorkshopDateTime,
  statusToneClass,
  todayYmdLocal,
  WORKSHOP_BOARD_COLUMNS,
  WORKSHOP_STATUS_LABELS,
} from "../lib/workshop-labels";

type BoardFilter = "all" | WorkshopJobStatusDTO;

function JobCard({ shopId, job }: { shopId: string; job: WorkshopJobListRow }) {
  return (
    <Link
      to="/app/shops/$shopId/workshop/jobs/$jobId"
      params={{ shopId, jobId: job.id }}
      className={cn(
        "block rounded-2xl border border-border bg-card p-3.5 text-left shadow-sm transition",
        "hover:border-primary/40 hover:shadow-md active:scale-[0.99]",
        "touch-manipulation",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-xl tracking-wide">{job.plateNumber}</p>
          <p className="mt-0.5 font-mono text-muted-foreground text-xs">
            {job.jobNumber} · ຮັບ {formatWorkshopDateTime(job.createdAt)}
          </p>
        </div>
        <Badge
          className={cn(
            "shrink-0 border px-2.5 py-1 text-[11px]",
            statusToneClass(job.status),
          )}
          variant="secondary"
        >
          {WORKSHOP_STATUS_LABELS[job.status]}
        </Badge>
      </div>

      <div className="mt-3 flex items-center gap-2 text-sm">
        <UserIcon className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate font-medium">{job.customerName}</span>
        <span className="shrink-0 text-muted-foreground">·</span>
        <PhoneIcon className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate text-muted-foreground text-xs">
          {job.customerPhone}
        </span>
      </div>

      {job.complaint ? (
        <p className="mt-2 line-clamp-2 border-border border-t border-dashed pt-2 text-muted-foreground text-xs leading-relaxed">
          {job.complaint}
        </p>
      ) : null}
    </Link>
  );
}

function StatusChip({
  active,
  label,
  count,
  onClick,
}: {
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full border px-3.5 font-medium text-sm touch-manipulation transition",
        active
          ? "border-primary bg-primary text-primary-foreground shadow-sm"
          : "border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground",
      )}
    >
      {label}
      <span
        className={cn(
          "rounded-full px-1.5 py-0.5 font-bold text-xs tabular-nums",
          active ? "bg-primary-foreground/20" : "bg-muted text-foreground",
        )}
      >
        {count}
      </span>
    </button>
  );
}

export function ShopWorkshopPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate();
  const { currentPermissions, currentShop } = useMerchantShops(shopId);
  const canCreate = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.workshop.create,
    currentShop?.verifiedLevel ?? null,
  );

  const [todayOnly, setTodayOnly] = useState(true);
  const [statusFilter, setStatusFilter] = useState<BoardFilter>("all");
  const [search, setSearch] = useState("");
  const today = todayYmdLocal();

  const listQuery = useMemo(
    () => {
      const branchId =
        (shopId && getStoredMerchantPosBranchId(shopId)) ||
        currentShop?.branchId ||
        undefined;
      const filterBranch =
        hasShopFeature(currentShop?.featureFlags, "multi_branch") && branchId
          ? branchId
          : undefined;
      return {
        status: "board" as const,
        limit: 100,
        offset: 0,
        q: search.trim() || undefined,
        ...(filterBranch ? { branchId: filterBranch } : {}),
        ...(todayOnly ? { dateFrom: today, dateTo: today } : {}),
      };
    },
    [
      todayOnly,
      today,
      search,
      shopId,
      currentShop?.branchId,
      currentShop?.featureFlags,
    ],
  );

  const jobsQ = useWorkshopJobsQuery(shopId, listQuery);
  const jobs = jobsQ.data?.data ?? [];

  const byStatus = useMemo(() => {
    const map: Record<string, WorkshopJobListRow[]> = {};
    for (const col of WORKSHOP_BOARD_COLUMNS) map[col] = [];
    for (const job of jobs) {
      const list = map[job.status];
      if (list) list.push(job);
    }
    return map;
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    if (statusFilter === "all") return jobs;
    return byStatus[statusFilter] ?? [];
  }, [jobs, byStatus, statusFilter]);

  function goNew() {
    if (!shopId) return;
    void nav({
      to: "/app/shops/$shopId/workshop/jobs/new",
      params: { shopId },
    });
  }

  return (
    <RequireMerchantPermission permission={MerchantPermissions.workshop.read}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.workshop(shopId)} />
      </Header>
      <Main className="pb-24 md:pb-6">
        <div className="flex flex-col gap-4 py-2 md:py-4">
          {/* Header */}
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2.5">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <WrenchIcon className="size-5" />
                </span>
                <div className="min-w-0">
                  <h1 className="font-bold text-xl tracking-tight md:text-2xl">
                    ວຽກສ້ອມ
                  </h1>
                  <p className="text-muted-foreground text-xs md:text-sm">
                    {todayOnly ? "ຄິວງານມື້ນີ້" : "ວຽກທີ່ເປີດຢູ່ທັງໝົດ"}
                    {currentShop?.shopName
                      ? ` · ${currentShop.shopName}`
                      : null}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="flex rounded-full border border-border bg-muted/40 p-0.5">
                <button
                  type="button"
                  className={cn(
                    "h-9 rounded-full px-3.5 font-medium text-sm touch-manipulation transition",
                    todayOnly
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground",
                  )}
                  onClick={() => setTodayOnly(true)}
                >
                  ມື້ນີ້
                </button>
                <button
                  type="button"
                  className={cn(
                    "h-9 rounded-full px-3.5 font-medium text-sm touch-manipulation transition",
                    !todayOnly
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground",
                  )}
                  onClick={() => setTodayOnly(false)}
                >
                  ທັງໝົດ
                </button>
              </div>
              {shopId ? (
                <Button
                  type="button"
                  variant="outline"
                  className="h-10"
                  onClick={() =>
                    void nav({
                      to: "/app/shops/$shopId/workshop/quotes",
                      params: { shopId },
                    })
                  }
                >
                  <FileTextIcon className="size-4" />
                  ໃບປະເມີນ
                </Button>
              ) : null}
              {canCreate && shopId ? (
                <Button
                  type="button"
                  className="hidden h-10 md:inline-flex"
                  onClick={goNew}
                >
                  <PlusIcon className="size-4" />
                  ໃບວຽກໃໝ່
                </Button>
              ) : null}
            </div>
          </div>

          {/* Search */}
          <div className="relative max-w-md">
            <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ຄົ້ນທະເບຽນ / ຊື່ / ເບີໂທ…"
              className="h-11 rounded-xl pl-9"
            />
          </div>

          {/* Status chips */}
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
            <StatusChip
              active={statusFilter === "all"}
              label="ທັງໝົດ"
              count={jobs.length}
              onClick={() => setStatusFilter("all")}
            />
            {WORKSHOP_BOARD_COLUMNS.map((status) => (
              <StatusChip
                key={status}
                active={statusFilter === status}
                label={WORKSHOP_STATUS_LABELS[status]}
                count={(byStatus[status] ?? []).length}
                onClick={() => setStatusFilter(status)}
              />
            ))}
          </div>

          {jobsQ.isLoading ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="h-28 animate-pulse rounded-2xl bg-muted"
                />
              ))}
            </div>
          ) : jobsQ.isError ? (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-8 text-center">
              <p className="font-medium text-destructive text-sm">
                ໂຫຼດກະດານງານບໍ່ສຳເລັດ
              </p>
              <Button
                type="button"
                variant="outline"
                className="mt-3"
                onClick={() => void jobsQ.refetch()}
              >
                ລອງໃໝ່
              </Button>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border px-4 py-12 text-center">
              <WrenchIcon className="mx-auto size-8 text-muted-foreground/50" />
              <p className="mt-3 font-medium text-sm">ຍັງບໍ່ມີໃບງານ</p>
              <p className="mt-1 text-muted-foreground text-xs">
                {search.trim()
                  ? "ບໍ່ພົບຜົນຄົ້ນ — ລອງປ່ຽນຄຳຄົ້ນ"
                  : todayOnly
                    ? "ສ້າງໃບວຽກໃໝ່ ຫຼື ສະຫຼັບເບິ່ງ «ທັງໝົດ»"
                    : "ສ້າງໃບວຽກໃໝ່ເພື່ອເລີ່ມຮັບລົດ"}
              </p>
              {canCreate ? (
                <Button type="button" className="mt-4" onClick={goNew}>
                  <PlusIcon className="size-4" />
                  ໃບວຽກໃໝ່
                </Button>
              ) : null}
            </div>
          ) : (
            <>
              {/* Desktop kanban — when viewing all statuses */}
              {statusFilter === "all" ? (
                <div className="hidden gap-3 lg:grid lg:grid-cols-4">
                  {WORKSHOP_BOARD_COLUMNS.map((status) => {
                    const colJobs = byStatus[status] ?? [];
                    return (
                      <section
                        key={status}
                        className="flex min-h-72 flex-col rounded-2xl border border-border bg-muted/20"
                      >
                        <header className="sticky top-0 z-10 flex items-center justify-between gap-2 rounded-t-2xl border-border border-b bg-background/95 px-3 py-2.5 backdrop-blur">
                          <h2 className="font-semibold text-sm">
                            {WORKSHOP_STATUS_LABELS[status]}
                          </h2>
                          <Badge variant="secondary" className="tabular-nums">
                            {colJobs.length}
                          </Badge>
                        </header>
                        <div className="flex flex-1 flex-col gap-2.5 p-2.5">
                          {colJobs.map((job) =>
                            shopId ? (
                              <JobCard key={job.id} shopId={shopId} job={job} />
                            ) : null,
                          )}
                          {colJobs.length === 0 ? (
                            <p className="px-1 py-6 text-center text-muted-foreground text-xs">
                              ບໍ່ມີວຽກ
                            </p>
                          ) : null}
                        </div>
                      </section>
                    );
                  })}
                </div>
              ) : null}

              {/* Mobile / filtered list / tablet */}
              <div
                className={cn(
                  "grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3",
                  statusFilter === "all" && "lg:hidden",
                )}
              >
                {filteredJobs.map((job) =>
                  shopId ? (
                    <JobCard key={job.id} shopId={shopId} job={job} />
                  ) : null,
                )}
              </div>
            </>
          )}
        </div>

        {/* Mobile FAB */}
        {canCreate && shopId ? (
          <button
            type="button"
            onClick={goNew}
            className={cn(
              "fixed right-4 z-30 flex h-14 items-center gap-2 rounded-full bg-primary px-5 font-bold text-primary-foreground text-sm shadow-lg",
              "bottom-[calc(4.75rem+env(safe-area-inset-bottom))] md:hidden",
              "touch-manipulation active:scale-95",
            )}
          >
            <PlusIcon className="size-5" />
            ໃບວຽກໃໝ່
          </button>
        ) : null}
      </Main>
    </RequireMerchantPermission>
  );
}
